import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { GetISODate } from "shared/helpers/DateHelpers";
import { LogError } from "components/AppInsights/TelemetryService";
import {
    GetSanitizedTelemetryError,
    GetSafeTelemetryProperties,
} from "shared/helpers/AxiosHelpers";

// Pure branch logic, exported for unit testing without a React/query-client/okta context.
// Callers that pass directParameters (POST-based grids and any caller opting out of the legacy
// URL-parse path) always get a fallback request built, even when the failed request carried no
// HTTP response at all (client/infra-level timeout, connection reset) — inputError.request.responseURL
// is only required for the legacy GET-URL-parsing path below.
export function buildServerSideExportRequestParams(inputError, optionalDirectParameters = null) {
    if (optionalDirectParameters) {
        return { params: optionalDirectParameters };
    }

    const hasResponseUrl = !!(inputError && inputError.request && inputError.request.responseURL);

    if (!hasResponseUrl) {
        return {
            error: new Error(
                "Server-side export fallback skipped: the failed request carried no responseURL and no directParameters were provided."
            ),
        };
    }

    // Parse the URL to extract the endpoint and parameters
    const url = new URL(inputError.request.responseURL);
    const params = Object.fromEntries(url.searchParams.entries());

    if (Object.keys(params).length === 0) {
        // POST endpoints carry parameters in the request body, not the URL.
        // Posting an empty body would fail server-side validation silently.
        return {
            error: new Error(
                "Server-side export skipped: no directParameters provided and the failed request URL has no query parameters. The caller must pass directParameters for POST-based grids."
            ),
        };
    }

    if (params.take !== undefined) {
        params.take = 0;
    }
    if (params.skip !== undefined) {
        params.skip = 0;
    }

    // Every query-string value parses as a string, but the export endpoints bind [FromBody] with
    // System.Text.Json, which rejects "true"/"false" for a nullable bool. Restore the real boolean
    // so filter flags survive the fallback round trip.
    if (params.payerSetupStatus !== undefined) {
        params.payerSetupStatus = params.payerSetupStatus === "true";
    }

    for (const key in params) {
        if (
            params.hasOwnProperty(key) &&
            !isNaN(Date.parse(params[key])) &&
            key.toLowerCase().includes("date")
        ) {
            params[key] = GetISODate(params[key]);
        }
    }

    return { params };
}

// Builds the fallback request (or logs+throws when it cannot be built) and posts it. Exported for
// unit testing with a mocked axios/token, independent of the useMutation/useOktaAuth wiring.
export async function postServerSideExportRequest(inputError, endpoint, optionalDirectParameters, accessToken) {
    const { params, error } = buildServerSideExportRequestParams(inputError, optionalDirectParameters);

    if (error) {
        LogError(error, {
            endpoint,
            ...GetSafeTelemetryProperties(inputError),
        });
        throw error;
    }

    try {
        return await axios.post(endpoint, params, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    } catch (e) {
        // Log a sanitized error: the raw axios error carries the request config and response body.
        LogError(GetSanitizedTelemetryError(e), {
            message: "Server-side export queue insert failed",
            endpoint,
            ...GetSafeTelemetryProperties(e),
        });
        throw e;
    }
}

export function useInsertServerSideExport() {
    const { oktaAuth } = useOktaAuth();

    return useMutation({
        mutationFn: async (mutateParameter) => {
            if (mutateParameter.optionalCallback) {
                mutateParameter.optionalCallback();
            }

            let accessToken = oktaAuth.getAccessToken();

            return await postServerSideExportRequest(
                mutateParameter.error,
                mutateParameter.endpoint,
                mutateParameter.directParameters,
                accessToken
            );
        },
    });
}
